/** @jsxImportSource @emotion/react */
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomGrid from 'components/grids/CustomGrid';
import { IconButton } from 'components/buttons/IconSVG';
import { findExcpByElmPrjId, findScheduleDelayStatus } from 'apis/setup/SetupSchedule';
import { getProgressRate } from 'utils/SetupUtil';
import { CommonUtil } from 'utils/CommonUtil';
import { findProjectById } from 'apis/setup/SetupProject';
import { SetupProject } from 'models/setup/SetupProject';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { Code } from 'models/common/CommonCode';
import { SetupSchedule } from 'models/setup/SetupSchedule';
import { PageMode } from 'models/common/Common';
import useEvent from 'react-use-event-hook';
import ProjectIssueCreatePage from '../issue/ProjectIssueRegisterPage';
import { findProjectAllStep } from 'apis/setup/SetupStep';

type Props = {
  open: boolean;
  close: () => void;
  condition: {
    elmPrjId: string; // 프로젝트ID
    elmPrjNm?: string; // 프로젝트명
  };
};

/**
 * @desc 설비셋업관리 > 셋업일정지연현황 팝업
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.03.09   jshwang12     신규 생성
 * 2026.05.06   jihwan89      이슈사항 있을 시 실적 컬럼 버튼 추가 및 팝업 노출 / 컬럼 더블클릭시 이슈 작성 팝업 노출
 * 2026.07.01   jshwang12     설비단계자동설정 기능 적용 (공통코드에서 테이블로 변경)
 */
const SetupScheduleDelayStatusPopup = ({ open, close, ...props }: Props) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const [rowData, setRowData] = useState<any[]>([]);
  const [elmPrjNm, setElmPrjNm] = useState<string | undefined>(props?.condition?.elmPrjNm);
  const [prjExcpData, setPrjExcpData] = useState<SetupSchedule[]>([]);
  const defaultCondition = {
    elmPrjId: props?.condition?.elmPrjId,
    eqpId: '',
    eqpNm: '',
    excpId: '',
    prjSpCds: [],
  };
  const [condition, setCondition] = useState<any>(defaultCondition);
  const [code, setCode] = useState<any>({});

  const [openIssuePopup, setOpenIssuePopup] = useState<boolean>(false);
  const [issuePopupCondition, setIssuePopupCondition] = useState<any>({});
  const [openIssuePage, setOpenIssuePage] = useState<boolean>(false);
  const [issuePageCondition, setIssuePageCondition] = useState<any>({});

  useEffect(() => {
    if (condition?.elmPrjId) {
      if (!condition?.elmPrjNm) {
        findProjectById(condition.elmPrjId).then((result: SetupProject) => {
          setElmPrjNm(result?.elmPrjNm);
        });
      } else {
        setElmPrjNm(condition?.elmPrjNm);
      }
      getCommonCodes();
      handleSearch(condition);
      findExcpByElmPrjId(condition?.elmPrjId, '').then((result: SetupSchedule[]) => {
        setPrjExcpData(result);
      });
    } else {
      setPrjExcpData([]);
    }
  }, [condition?.elmPrjId]);

  const optionsPrjExcp = useMemo(() => {
    return (prjExcpData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.excpId,
          cmnCdNm: cur.excpNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [prjExcpData]);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'PRDN_PROC_CD_NM',
        header: String(t('setup.label.공정', '공정')),
        minWidth: 70,
        width: 93,
        align: 'center',
      },
      {
        binding: 'EQCL_ID',
        header: String(t('com.label.설비군', '설비군')),
        minWidth: 70,
        width: 80,
        align: 'center',
      },
      {
        binding: 'EQP_ID',
        header: String(t('setup.label.M-CODE', 'M-CODE')),
        minWidth: 80,
        width: 120,
        align: 'center',
      },
      {
        binding: 'EQP_NM',
        header: String(t('setup.label.설비명', '설비명')),
        minWidth: 80,
        width: '*',
        align: 'left',
      },
      {
        binding: 'EXCP_NM',
        header: String(t('setup.label.협력업체명', '협력업체명')),
        minWidth: 80,
        width: '*',
        align: 'left',
      },
      {
        binding: 'EXCP_ID',
        visible: false,
      },
      {
        binding: 'PRJ_SP_CD_NM',
        header: String(t('setup.label.단계', '단계')),
        minWidth: 80,
        width: '*',
        align: 'center',
      },
      {
        binding: 'PRJ_SP_CD',
        visible: false,
      },
      {
        header: String(t('setup.label.지연 현황', '지연 현황')),
        columns: [
          {
            binding: 'PLAN_ST_DT',
            header: String(t('setup.label.계획 시작일', '계획 시작일')),
            minWidth: 60,
            width: 108,
            align: 'center',
          },
          {
            binding: 'PLAN_END_DT',
            header: String(t('setup.label.계획 종료일', '계획 종료일')),
            minWidth: 60,
            width: 108,
            align: 'center',
          },
          {
            binding: 'PLAN_PROG_RT',
            header: String(t('setup.label.계획 진척률', '계획 진척률')),
            minWidth: 60,
            width: 108,
            align: 'center',
            cellTemplate: (params) => getProgressRate(params?.value),
          },
          {
            binding: 'PROG_RT',
            header: String(t('setup.label.실적', '실적')),
            minWidth: 60,
            width: 78,
            align: 'center',
            cssClass: 'WijmoIconButton',
            cellTemplate: (params) =>
              `${getProgressRate(params?.value)}${
                params.item.ISU_ID ? '<button class="issue"></button>' : ''
              }`,
          },
          {
            binding: 'ISU_ID',
            visible: false,
          },
          {
            binding: 'GAP',
            header: String(t('setup.label.GAP', 'GAP')),
            minWidth: 60,
            width: 78,
            align: 'center',
            cellTemplate: (params) =>
              `<span style="color:red;">${getProgressRate(params?.value)}</span>`,
          },
        ],
      },
    ];
  }, []);

  const onInitialized = (grid) => {
    gridRef.current = grid;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row].dataItem;
      const binding = grid.columns[ht.col].binding;

      if (ht.panel === grid.cells) {
        // 현황
        if (e.target instanceof HTMLButtonElement) {
          if (e.target.classList.contains('issue')) {
            // 이슈 버튼 클릭 시 해당 설비/단계의 가장 최근 이슈로 이동
            const isuId = item['ISU_ID'];
            if (isuId) {
              setIssuePopupCondition({
                isuId: isuId,
              });
              setOpenIssuePopup(true);
            }
          }
        }
      }
    });

    grid.hostElement.addEventListener('dblclick', (e) => {
      // [26.04.14] 작업일보 > 일정현황상세 > 현황 더블클릭 시 해당 설비와 단계를 기본값으로 하는 이슈등록팝업을 노출
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const binding = grid.columns[ht.col].binding;
      if (ht.panel === grid.cells) {
        const item = grid.rows[ht.row].dataItem;
        // 현황
        onOpenCreateIssuePage?.(
          item['PRJ_SP_CD'],
          item['TGT_EQP_ID'],
          item['EQP_ID'],
          item['EQP_NM'],
          item['EQCL_ID'],
          item['EXCP_ID']
        );
      }
    });

    gridRef.current?.refresh();
  };

  const onOpenCreateIssuePage = useEvent((prjSpCd, tgtEqpId, eqpId, eqpNm, eqclId, excpId) => {
    // 수정버튼 노출 조건과 동일
    console.log('prjSpCd', prjSpCd);
    console.log('tgtEqpId', tgtEqpId);
    console.log('eqpId', eqpId);
    console.log('eqpNm', eqpNm);
    console.log('eqclId', eqclId);
    console.log('excpId', excpId);
    if (prjSpCd && tgtEqpId && eqpId && eqpNm && eqclId && excpId) {
      setIssuePageCondition({
        prjSpCd: prjSpCd,
        tgtEqpId: tgtEqpId,
        eqpId: eqpId,
        eqpNm: eqpNm,
        eqclId: eqclId,
        excpId: excpId,
      });
      setOpenIssuePage(true);
    }
  });

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.일정지연현황', '일정지연현황'),
      t('setup.label.일정지연현황', '일정지연현황')
    );
  };

  const title = useMemo(() => {
    const arr = [t('setup.label.셋업 일정 지연 현황', '셋업 일정 지연 현황'), elmPrjNm].filter(
      (o) => !!o
    );

    return arr.join(' : ');
  }, [elmPrjNm]);

  const handleSearch = (params) => {
    if (params?.elmPrjId) {
      findScheduleDelayStatus(params).then((result) => {
        setRowData(result);
      });
    } else {
      setRowData([]);
    }
  };

  const getCommonCodes = () => {
    if (condition?.elmPrjId) {
      findProjectAllStep(condition?.elmPrjId).then((result) => {
        setCode({
          prjSpCd: (result || []).reduce(
            (acc, cur) => [...acc, { cmnCd: cur.prjSpCd, cmnCdNm: cur.prjSpNm }],
            [] as Code[]
          ),
        });
      });
    }
  };

  const handleOnChange = (name, value) => {
    setCondition((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(condition);
    }
  };

  return (
    <CustomDialog
      title={String(t('setup.label.셋업 일정 지연 현황', '셋업 일정 지연 현황'))}
      size={'lg'}
      open={open}
      onClose={close}
    >
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols className="width25">
                <label>
                  <span>{String(t('setup.label.M-CODE', 'M-CODE'))}</span>
                </label>
                <CustomInputWithSearch
                  name="eqpId"
                  placeholder={String(
                    t('setup.label.M-CODE를 입력해 주세요.', 'M-CODE를 입력해 주세요.')
                  )}
                  value={condition?.eqpId}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols className="width25">
                <label>
                  <span>{String(t('setup.label.설비명', '설비명'))}</span>
                </label>
                <CustomInputWithSearch
                  name="eqpNm"
                  placeholder={String(
                    t('setup.label.설비명을 입력해 주세요.', '설비명을 입력해 주세요.')
                  )}
                  value={condition.eqpNm}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols className="width25">
                <label>
                  <span>{String(t('setup.label.협력업체명', '협력업체명'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={optionsPrjExcp}
                  defaultValue={condition?.excpId}
                  onChange={(value) => {
                    handleOnChange('excpId', value);
                  }}
                />
              </SearchCols>
              <SearchCols className="width25">
                <label>
                  <span>{String(t('setup.label.단계', '단계'))}</span>
                </label>
                <MultiComboBox
                  id="prjSpCd"
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={code?.prjSpCd || []}
                  defaultValue={condition.prjSpCds || []}
                  onChange={(value) => {
                    handleOnChange('prjSpCds', value);
                  }}
                  className="width25"
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            defaultCondition={defaultCondition}
            setReset={setCondition}
            onSearch={() => handleSearch(condition)}
          />
        </SearchBoxRow>
      </SearchBox>

      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{title}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={`setupScheduleDelayStatusGrid`}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={443}
        isSelector={false}
        isReadOnly={true}
        initialized={onInitialized}
      />
      {openIssuePopup && (
        <ProjectIssueCreatePage
          open={openIssuePopup}
          close={() => {
            setOpenIssuePopup(false);
          }}
          onSearch={() => undefined}
          mode={PageMode.READ}
          isuId={issuePopupCondition.isuId || ''}
        />
      )}
      {openIssuePage && (
        <ProjectIssueCreatePage
          open={openIssuePage}
          close={() => {
            setOpenIssuePage(false);
            findScheduleDelayStatus(condition).then((result) => {
              const row = rowData.filter(
                (item) =>
                  item.EXCP_ID === issuePageCondition.excpId &&
                  item.PRJ_SP_CD === issuePageCondition.prjSpCd &&
                  item.TGT_EQP_ID === issuePageCondition.tgtEqpId &&
                  item.EQP_ID === issuePageCondition.eqpId &&
                  item.EQP_NM === issuePageCondition.eqpNm &&
                  item.EQCL_ID === issuePageCondition.eqclId
              );

              const data = result.filter(
                (item) =>
                  item.EXCP_ID === row[0].EXCP_ID &&
                  item.PRJ_SP_CD === row[0].PRJ_SP_CD &&
                  item.TGT_EQP_ID === row[0].TGT_EQP_ID &&
                  item.EQP_ID === row[0].EQP_ID &&
                  item.EQP_NM === row[0].EQP_NM &&
                  item.EQCL_ID === row[0].EQCL_ID
              );

              row[0].ISU_ID = data[0].ISU_ID;
              gridRef.current.refresh();
            });
          }}
          mode={PageMode.WRITE}
          elmPrjId={condition?.elmPrjId}
          excpId={issuePageCondition?.excpId}
          prjSpCd={issuePageCondition?.prjSpCd}
          equip={{
            tgtEqpId: issuePageCondition?.tgtEqpId,
            eqpId: issuePageCondition?.eqpId,
            eqpNm: issuePageCondition?.eqpNm,
            eqclId: issuePageCondition?.eqclId,
          }}
        />
      )}
    </CustomDialog>
  );
};

export default SetupScheduleDelayStatusPopup;
